import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import Layout from "@components/Layout";
import Text from "@components/Text";
import Tooltip from "@components/Tooltip";
import Button from "@components/Button";
import Select from "@components/Select";
import SizedBox from "@components/SizedBox";
import { Row, Column } from "@components/Flex";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import styled from "@emotion/styled";
import { useLang } from "@src/hooks/useLang";
import { useStores } from "@stores";
import { listFarms, Farm } from "@services/farmsService";
import {
  AccessGroup,
  TeamMember,
  Invitation,
  listGroups,
  listMembers,
  listInvitations,
  updateMemberGroup,
  removeMember,
  revokeInvitation,
  resendInvitation,
} from "@services/teamService";
import GroupDialog from "./GroupDialog";
import InviteDialog from "./InviteDialog";
import { roleSummaryParts, RoleSummaryLabels } from "./roles";

const Card = styled.div`
  background: #ffffff;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.1);
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
`;

const Chip = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: bold;
  color: #68ad18;
  background: #eef6e3;
  border-radius: 4px;
  padding: 2px 8px;
`;

const ListRow = styled(Row)`
  padding: 12px 0;
  border-bottom: 1px solid #eef0f2;
  align-items: center;
`;

const SectionTitle: React.FC<{ children: React.ReactNode; extra?: React.ReactNode }> = ({
  children,
  extra,
}) => (
  <Row alignItems="center" justifyContent="space-between" style={{ marginBottom: 12 }}>
    <Text size={18} type="heading" color="#494C5B" fitContent nowrap>
      {children}
    </Text>
    {extra}
  </Row>
);

const TeamScreen: React.FC = () => {
  const lang = useLang();
  const t = (lang as any).team ?? {};
  const { accountStore } = useStores();
  // Team management (invite / groups / reassign) is a paid feature, and it
  // must be the user's own subscription; viewing and removing access stay
  // available so a lapsed owner is never locked out.
  const paid = accountStore.canManageTeam;

  const [farms, setFarms] = useState<Farm[]>([]);
  const [groups, setGroups] = useState<AccessGroup[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  const [groupDialog, setGroupDialog] = useState<{ open: boolean; group: AccessGroup | null }>(
    { open: false, group: null }
  );
  const [inviteOpen, setInviteOpen] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [f, g, m, inv] = await Promise.all([
        listFarms(),
        listGroups(),
        listMembers(),
        listInvitations(),
      ]);
      setFarms(f);
      setGroups(g);
      setMembers(m);
      setInvitations(inv);
    } catch (e: any) {
      // The list calls toast through api.ts already.
      console.error("Failed to load team", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (accountStore.isViewingOwnAccount) reload();
    else setLoading(false);
  }, [reload, accountStore.isViewingOwnAccount]);

  const groupNameById = (id: string) => groups.find((g) => g.id === id)?.name ?? "";

  const handleReassign = async (memberUserId: string, groupId: string) => {
    try {
      await updateMemberGroup(memberUserId, groupId);
      toast(t.memberUpdated ?? "Updated", { type: "success" });
      reload();
    } catch (e: any) {
      toast(e.message ?? e.toString(), { type: "error" });
    }
  };

  const handleRemove = async (m: TeamMember) => {
    if (!window.confirm((t.removeConfirm ?? "Remove access for {email}?").replace("{email}", m.email)))
      return;
    try {
      await removeMember(m.memberUserId);
      toast(t.memberRemoved ?? "Access removed", { type: "success" });
      reload();
    } catch (e: any) {
      toast(e.message ?? e.toString(), { type: "error" });
    }
  };

  const handleRevoke = async (inv: Invitation) => {
    try {
      await revokeInvitation(inv.id);
      reload();
    } catch (e: any) {
      toast(e.message ?? e.toString(), { type: "error" });
    }
  };

  const handleResend = async (inv: Invitation) => {
    try {
      await resendInvitation(inv.id);
      toast(t.inviteResent ?? "Invitation re-sent", { type: "success" });
    } catch (e: any) {
      toast(e.message ?? e.toString(), { type: "error" });
    }
  };

  const summaryLabels: RoleSummaryLabels = {
    allFarms: t.allFarmsShort ?? "All farms",
    farmsCount: `{n} ${t.farmsWord ?? "farms"}`,
    fields: t.capViewFieldsShort ?? "Fields",
    loads: t.capRecordLoadsShort ?? "Loads",
    calibrate: t.capCalibrateShort ?? "Calibration",
    machines: t.capDevicesShort ?? "Machines",
    edit: t.capEditShort ?? "Edit",
    del: t.capDeleteShort ?? "Delete",
    exp: t.capExportShort ?? "Export",
    webOnly: t.clientWebShort ?? "Web only",
    appOnly: t.clientAppShort ?? "Driver app only",
  };
  const farmNameById = (id: string) => farms.find((f) => f.id === id)?.name;
  const capChips = (g: AccessGroup) => roleSummaryParts(g, farmNameById, summaryLabels);
  // One line under each person, e.g. "Farm 2 · Loads · Calibration · Driver app only".
  const memberRoleSummary = (m: TeamMember) => {
    const g = groups.find((x) => x.id === m.groupId);
    return g ? capChips(g).join(" · ") : "";
  };

  return (
    <Layout
      title={t.title ?? "Team accounts"}
      subtitle={t.subtitle ?? "Invite people and control what they can access"}
    >
      {!accountStore.isViewingOwnAccount ? (
        <Card>
          <Text size={15} color="#899093">
            {t.ownerOnly ?? "Switch to your own account to manage team access."}
          </Text>
        </Card>
      ) : loading ? (
        <Text size={15} color="#899093">
          {t.loading ?? "Loading…"}
        </Text>
      ) : (
        <Column style={{ gap: 24, maxWidth: 760 }}>
          {!paid && (
            <Card>
              <Text size={14} color="#899093">
                {t.subscriptionRequired ??
                  "Team management is part of the paid subscription. You can still see your team and remove access, but inviting people and editing groups requires an active subscription."}
              </Text>
            </Card>
          )}
          {/* Groups */}
          <Card>
            <SectionTitle
              extra={
                paid ? (
                  <Button
                    onClick={() => setGroupDialog({ open: true, group: null })}
                    style={{ width: "fit-content", padding: "0 16px" }}
                  >
                    {t.newGroup ?? "New group"}
                  </Button>
                ) : undefined
              }
            >
              {t.groups ?? "Groups"}
            </SectionTitle>
            {groups.length === 0 ? (
              <Text size={14} color="#899093">
                {t.noGroups ?? "No groups yet. Create one to start inviting people."}
              </Text>
            ) : (
              groups.map((g) => (
                <ListRow key={g.id} justifyContent="space-between">
                  <Column style={{ flex: 1, overflow: "hidden" }}>
                    <Text size={15} type="heading" color="#494C5B" nowrap>
                      {g.name}
                    </Text>
                    <Row style={{ gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                      {capChips(g).map((c) => (
                        <Chip key={c}>{c}</Chip>
                      ))}
                    </Row>
                  </Column>
                  <Row mainAxisSize="fit-content" alignItems="center" style={{ gap: 12 }}>
                    <Text size={13} color="#899093" nowrap fitContent>
                      {(t.memberCount ?? "{n} people").replace("{n}", String(g.memberCount))}
                    </Text>
                    {paid && (
                      <Text
                        size={14}
                        color="#68AD18"
                        fitContent
                        nowrap
                        style={{ cursor: "pointer" }}
                        onClick={() => setGroupDialog({ open: true, group: g })}
                      >
                        {t.edit ?? "Edit"}
                      </Text>
                    )}
                  </Row>
                </ListRow>
              ))
            )}
          </Card>

          {/* People */}
          <Card>
            <SectionTitle
              extra={
                paid ? (
                  <Button
                    onClick={() => {
                      if (groups.length === 0) {
                        toast(t.createGroupFirst ?? "Create a group first.", { type: "error" });
                        return;
                      }
                      setInviteOpen(true);
                    }}
                    style={{ width: "fit-content", padding: "0 16px" }}
                  >
                    {t.invite ?? "Invite"}
                  </Button>
                ) : undefined
              }
            >
              {t.people ?? "People"}
            </SectionTitle>

            {invitations.length > 0 && (
              <>
                <Text size={13} color="#899093" style={{ marginBottom: 4 }}>
                  {t.pending ?? "Pending invitations"}
                </Text>
                {invitations.map((inv) => (
                  <ListRow key={inv.id} justifyContent="space-between">
                    <Column style={{ flex: 1, overflow: "hidden" }}>
                      <Text size={14} color="#494C5B" nowrap>
                        {inv.email}
                      </Text>
                      <Row alignItems="center" mainAxisSize="fit-content">
                        <Text size={12} color="#899093" fitContent nowrap>
                          {inv.groupName}
                          {" · "}
                          {(t.inviteExpires ?? "Expires")}{" "}
                          {moment(inv.expiresAt).format("MMM D, YYYY")}
                        </Text>
                        <SizedBox width={4} />
                        <Tooltip
                          contentWrapperStyles={{ maxWidth: 260 }}
                          content={
                            <Text size={12} color="#494C5B">
                              {t.inviteExpiryHint ??
                                "If this person hasn't created their account by this date, the invitation expires and you'll need to resend it. Signing up with the invited email is enough — the email link is optional."}
                            </Text>
                          }
                        >
                          <Text size={12} color="#899093" fitContent>
                            ⓘ
                          </Text>
                        </Tooltip>
                      </Row>
                    </Column>
                    <Row mainAxisSize="fit-content" alignItems="center" style={{ gap: 14 }}>
                      {paid && (
                        <Text
                          size={13}
                          color="#68AD18"
                          fitContent
                          nowrap
                          style={{ cursor: "pointer" }}
                          onClick={() => handleResend(inv)}
                        >
                          {t.resend ?? "Resend"}
                        </Text>
                      )}
                      <Text
                        size={13}
                        color="#e5484d"
                        fitContent
                        nowrap
                        style={{ cursor: "pointer" }}
                        onClick={() => handleRevoke(inv)}
                      >
                        {t.revoke ?? "Revoke"}
                      </Text>
                    </Row>
                  </ListRow>
                ))}
                <SizedBox height={16} />
              </>
            )}

            {members.length === 0 && invitations.length === 0 ? (
              <Text size={14} color="#899093">
                {t.noPeople ?? "No one invited yet."}
              </Text>
            ) : (
              members.map((m) => (
                <ListRow key={m.memberUserId} justifyContent="space-between">
                  <Column style={{ flex: 1, overflow: "hidden" }}>
                    <Text size={14} type="heading" color="#494C5B" nowrap>
                      {m.name || m.email}
                    </Text>
                    <Text size={12} color="#899093" nowrap>
                      {m.email}
                    </Text>
                    {memberRoleSummary(m) && (
                      <Text
                        size={12}
                        color="#68AD18"
                        style={{ lineHeight: "16px", wordBreak: "break-word" }}
                      >
                        {memberRoleSummary(m)}
                      </Text>
                    )}
                  </Column>
                  <Row mainAxisSize="fit-content" alignItems="center" style={{ gap: 12 }}>
                    {paid ? (
                      <div style={{ width: 210 }}>
                        <Select
                          options={groups.map((g) => ({ title: g.name, value: g.id }))}
                          value={m.groupId}
                          onChange={(gid) => handleReassign(m.memberUserId, gid)}
                          renderValue={() => (
                            <Text
                              size={13}
                              color="#494C5B"
                              style={{ lineHeight: "16px", wordBreak: "break-word" }}
                            >
                              {groupNameById(m.groupId) || m.groupName}
                            </Text>
                          )}
                        />
                      </div>
                    ) : (
                      <Text
                        size={13}
                        color="#899093"
                        fitContent
                        style={{ maxWidth: 210, lineHeight: "16px", wordBreak: "break-word" }}
                      >
                        {groupNameById(m.groupId) || m.groupName}
                      </Text>
                    )}
                    <Text
                      size={13}
                      color="#e5484d"
                      fitContent
                      nowrap
                      style={{ cursor: "pointer" }}
                      onClick={() => handleRemove(m)}
                    >
                      {t.remove ?? "Remove"}
                    </Text>
                  </Row>
                </ListRow>
              ))
            )}
          </Card>
        </Column>
      )}

      <GroupDialog
        visible={groupDialog.open}
        group={groupDialog.group}
        farms={farms}
        onClose={() => setGroupDialog({ open: false, group: null })}
        onSaved={reload}
      />
      <InviteDialog
        visible={inviteOpen}
        groups={groups}
        onClose={() => setInviteOpen(false)}
        onInvited={reload}
      />
    </Layout>
  );
};

export default observer(TeamScreen);
